import { useCallback } from "react";
import { useReducerLocalStorage } from "./hooks/use-localstorage-state";

export type PassengersControl = {
  value: number;
  decrement: () => void;
  isDecrementDisabled: boolean;
  increment: () => void;
  isIncrementDisabled: boolean;
};
export type PassengerCounts = {
  adults: number;
  children: number;
  infants: number;
};

type ActionType = "increment" | "decrement";

export const MAX_CHILDREN_WITHOUT_ADULT = 3;
export const MAX_INFANTS = 3;
export const MAX_TRAVELERS = 7;
export const usePassengers = () => {
  const [state, dispatch] = useReducerLocalStorage(
    "passengers",
    (
      current_,
      [type, key]: [ActionType, "adults" | "children" | "infants"],
    ) => {
      const current = globalThis.structuredClone(current_);

      if (type === "increment") {
        current[key] = current[key] + 1;
      }
      if (type === "decrement") {
        current[key] = current[key] - 1;
      }

      current.totalPassengers = current.adults + current.children;

      current.isDecrementAdultsEnabled = current.adults > 0;
      current.isIncrementAdultsEnabled =
        current.adults < 7 && current.totalPassengers < MAX_TRAVELERS;

      current.isDecrementChildrenEnabled = current.children > 0;
      current.isIncrementChildrenEnabled =
        current.children <
          (current.adults > 0 ? 7 : MAX_CHILDREN_WITHOUT_ADULT) &&
        current.totalPassengers < MAX_TRAVELERS;

      if (
        current.adults <= 0 &&
        current.children > MAX_CHILDREN_WITHOUT_ADULT
      ) {
        current.children = Math.min(
          current.children,
          MAX_CHILDREN_WITHOUT_ADULT,
        );
      }

      if (current.children === 0 && current.adults <= 1) {
        current.adults = 1;
        current.isDecrementAdultsEnabled = false;
      }

      current.isDecrementInfantsEnabled =
        current.infants > 0 && current.adults > 0;
      current.isIncrementInfantsEnabled =
        current.infants < MAX_INFANTS && current.infants < current.adults;

      if (current.infants > current.adults) {
        current.infants = current.adults;
      }

      return current;
    },
    {
      adults: 1,
      children: 0,
      infants: 0,
      isDecrementAdultsEnabled: false,
      isDecrementChildrenEnabled: false,
      isDecrementInfantsEnabled: false,
      isIncrementAdultsEnabled: true,
      isIncrementChildrenEnabled: true,
      isIncrementInfantsEnabled: true,
      totalPassengers: 0,
    },
  );

  return {
    controlAdults: {
      decrement: useCallback(() => {
        dispatch(["decrement", "adults"]);
      }, [dispatch]),
      increment: useCallback(() => {
        dispatch(["increment", "adults"]);
      }, [dispatch]),
      isDecrementDisabled: !state.isDecrementAdultsEnabled,
      isIncrementDisabled: !state.isIncrementAdultsEnabled,
      value: state.adults,
    } satisfies PassengersControl,
    controlChildren: {
      decrement: useCallback(() => {
        dispatch(["decrement", "children"]);
      }, [dispatch]),
      increment: useCallback(() => {
        dispatch(["increment", "children"]);
      }, [dispatch]),
      isDecrementDisabled: !state.isDecrementChildrenEnabled,
      isIncrementDisabled: !state.isIncrementChildrenEnabled,
      value: state.children,
    } satisfies PassengersControl,
    controlInfants: {
      decrement: useCallback(() => {
        dispatch(["decrement", "infants"]);
      }, [dispatch]),
      increment: useCallback(() => {
        dispatch(["increment", "infants"]);
      }, [dispatch]),
      isDecrementDisabled: !state.isDecrementInfantsEnabled,
      isIncrementDisabled: !state.isIncrementInfantsEnabled,
      value: state.infants,
    } satisfies PassengersControl,
  };
};
